import { useState, useEffect, useRef, useCallback } from 'react';

export enum ScrollDirection {
    up = 'up',
    down = 'down',
}

export const useScrollDirection = (containerRef: React.RefObject<HTMLDivElement> | null, threshold?: number) => {
    const [scrollDirection, setScrollDir] = useState<ScrollDirection>(ScrollDirection.up);
    const previousScrollYPosition = useRef<number>(0);
    const lockTimer = useRef<number | null>(null);

    threshold = threshold || 100;

    const updateScrollDirection = useCallback(() => {
        const container = containerRef?.current;
        if (container) {
            const currentScrollYPosition = container.scrollTop;

            const scrolledMoreThanThreshold =
                Math.abs(currentScrollYPosition - previousScrollYPosition.current) > threshold;

            if (scrolledMoreThanThreshold) {
                const newScrollDirection =
                    currentScrollYPosition > previousScrollYPosition.current
                        ? ScrollDirection.down
                        : ScrollDirection.up;
                setScrollDir(newScrollDirection);
                previousScrollYPosition.current = currentScrollYPosition > 0 ? currentScrollYPosition : 0;
            }
        }
    }, [containerRef, threshold]);

    useEffect(() => {
        const container = containerRef?.current;
        if (container) {
            const handleScroll = () => {
                if (lockTimer.current === null) {
                    updateScrollDirection();
                }
            };

            container.addEventListener('scroll', handleScroll, { passive: true });

            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [containerRef, updateScrollDirection]);

    // Lock the scroll direction for a specified duration, this is useful to combine with animations to avoid re-triggering the function while transitioning
    const lockScrollDirection = useCallback((duration: number) => {
        if (lockTimer.current !== null) {
            clearTimeout(lockTimer.current);
        }
        lockTimer.current = window.setTimeout(() => {
            lockTimer.current = null;
        }, duration);
    }, []);

    return { scrollDirection, lockScrollDirection };
};
